/**
 * In-app help center: /help (index) + /help/:slug (article). Public + crawlable.
 * Content lives in src/data/helpArticles.ts.
 */
import { BrandMark } from '../components/BrandMark';
import { Link, useParams, Navigate } from 'react-router-dom';
import {
  helpArticles,
  getHelpArticle,
  HelpBlock,
  HELP_AUTHOR,
  HELP_AUTHOR_URL,
  HELP_PUBLISHED,
  HELP_REVIEWED,
  HELP_INTRO,
  HELP_START_HERE,
  HELP_FAQS,
} from '../data/helpArticles';
import { useSeo } from '../hooks/useSeo';
// Shared with /blog on purpose. Help had NO inline markup at all, so a URL
// written in a paragraph rendered as dead plain text (found 12 Sept 2026 on
// /help/getting-started). One implementation, so the two page types cannot
// drift apart the way the prerenderer and React twin already have.
import { renderInline } from './Blog';

const SITE = 'https://agent-mesh.org';

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="border-b">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
          <Link to="/help" className="flex items-center gap-2">
            <BrandMark className="h-7 w-7" />
            <span className="text-lg font-bold">Agent<span className="text-brand">Mesh</span> Help</span>
          </Link>
          <Link
            to="/dashboard"
            className="text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            Back to app
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-5 py-12">{children}</main>
      <footer className="border-t">
        <div className="mx-auto max-w-3xl px-5 py-8 text-sm text-slate-500">
          {/* /example and /blog are listed here and in the prerendered footer in
              gen-content-pages.mjs. Both were orphans — in sitemap.xml and
              llms.txt, so AI crawlers found them, with no inbound link a person
              or Google's link graph could follow. Keep the two footers in step. */}
          <Link to="/help" className="hover:underline">All help articles</Link> ·{' '}
          <Link to="/example" className="hover:underline">See a real run</Link> ·{' '}
          {/* Plain <a>: prerendered HTML, no React route. */}
          <a href="/agent-library" className="hover:underline">Agents</a> ·{' '}
          <Link to="/blog" className="hover:underline">Writing</Link> ·{' '}
          <Link to="/dashboard" className="hover:underline">Dashboard</Link>
        </div>
      </footer>
    </div>
  );
}

function Block({ block }: { block: HelpBlock }) {
  if ('h' in block) return <h2 className="mt-6 mb-2 text-lg font-semibold">{block.h}</h2>;
  if ('h3' in block) return <h3 className="mt-5 mb-2 font-semibold">{block.h3}</h3>;
  if ('p' in block) return <p className="mb-3 text-slate-700">{renderInline(block.p)}</p>;
  if ('code' in block) {
    // Same treatment as the blog's code block; kept in step with the
    // prerenderer in scripts/dev/gen-content-pages.mjs.
    return (
      <pre className="mb-4 overflow-x-auto rounded-lg bg-[#0c1116] p-4 text-sm leading-6 text-slate-100">
        <code>{block.code}</code>
      </pre>
    );
  }
  if ('head' in block) {
    return (
      <div className="mb-4 overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              {block.head.map((h, i) => (
                <th key={i} className="border-b px-3 py-2 text-left font-semibold">{renderInline(h)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((r, i) => (
              <tr key={i}>
                {r.map((c, j) => (
                  <td key={j} className="border-b px-3 py-2 align-top text-slate-700">{renderInline(c)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if ('youtube' in block) {
    return (
      <figure className="mb-5">
        <div className="aspect-video w-full overflow-hidden rounded-lg border bg-slate-50">
          <iframe
            className="h-full w-full"
            src={`https://www.youtube-nocookie.com/embed/${block.youtube}`}
            title={block.title ?? 'AgentMesh walkthrough'}
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
        <figcaption className="mt-2 text-sm text-slate-500">
          {block.title ? `${block.title} · ` : ''}
          <a
            className="text-brand hover:underline"
            href={`https://youtu.be/${block.youtube}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Watch on YouTube ↗
          </a>
        </figcaption>
      </figure>
    );
  }
  return (
    <ol className="mb-4 list-decimal space-y-1 pl-6 text-slate-700">
      {block.steps.map((s, i) => (
        <li key={i}>{s}</li>
      ))}
    </ol>
  );
}

export function HelpIndex() {
  useSeo({
    title: 'Help Center',
    description:
      'AgentMesh help: how to create agents, build multi-agent workflows, automate with schedules and triggers, use tools, templates and plans.',
    canonical: `${SITE}/help`,
  });

  const categories = [...new Set(helpArticles.map((a) => a.category))];

  return (
    <Shell>
      <h1 className="mb-2 text-3xl font-bold">Help center</h1>
      {/* Intro, "where to start", FAQ and the byline all come from
          data/helpArticles.ts, which the prerenderer reads too. /help exists
          twice on separate code paths and every divergence bug here came from
          the two copies being written separately. */}
      <p className="mb-2 text-slate-600">{HELP_INTRO}</p>
      <p className="mb-8 text-sm text-slate-500">
        By{' '}
        <Link to="/about" className="text-brand hover:underline">
          {HELP_AUTHOR}
        </Link>{' '}
        · Last reviewed <time dateTime={HELP_REVIEWED}>{HELP_REVIEWED}</time>
      </p>

      <h2 className="mb-3 text-lg font-semibold">Where to start</h2>
      <div className="mb-8 overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <th className="border-b px-3 py-2 text-left font-semibold">If you…</th>
              <th className="border-b px-3 py-2 text-left font-semibold">Read this</th>
            </tr>
          </thead>
          <tbody>
            {HELP_START_HERE.map((r) => (
              <tr key={r.slug}>
                <td className="border-b px-3 py-2 align-top text-slate-700">{r.when}</td>
                <td className="border-b px-3 py-2 align-top">
                  <Link
                    to={`/help/${r.slug}`}
                    className="text-brand underline underline-offset-2 hover:no-underline"
                  >
                    {r.label}
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="mb-3 text-lg font-semibold">Frequently asked questions</h2>
      <div className="mb-8">
        {HELP_FAQS.map((f) => (
          <div key={f.q} className="mb-4">
            <h3 className="mb-1 font-semibold">{f.q}</h3>
            <p className="text-slate-700">{renderInline(f.a)}</p>
          </div>
        ))}
      </div>

      <p className="mb-8 text-sm text-slate-500">
        Also:{' '}
        <Link to="/compare" className="text-brand hover:underline">
          Compare AgentMesh
        </Link>{' '}
        ·{' '}
        <Link to="/solutions" className="text-brand hover:underline">
          AI agents by industry
        </Link>
      </p>

      <h2 className="mb-3 text-lg font-semibold">All guides</h2>
      {categories.map((cat) => (
        <div key={cat} className="mb-8">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
            {cat}
          </h2>
          <ul className="space-y-2">
            {helpArticles
              .filter((a) => a.category === cat)
              .map((a) => (
                <li key={a.slug}>
                  <Link
                    to={`/help/${a.slug}`}
                    className="block rounded-lg border p-4 hover:border-slate-400"
                  >
                    <span className="font-semibold">{a.title}</span>
                    <p className="mt-1 text-sm text-slate-600">{a.summary}</p>
                  </Link>
                </li>
              ))}
          </ul>
        </div>
      ))}
    </Shell>
  );
}

export function HelpArticlePage() {
  const { slug = '' } = useParams();
  const article = getHelpArticle(slug);

  useSeo({
    title: article ? `${article.title} · Help` : 'Help Center',
    description: article?.metaDescription || article?.summary,
    canonical: article ? `${SITE}/help/${article.slug}` : `${SITE}/help`,
    jsonLd: article
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.metaDescription || article.summary,
            url: `${SITE}/help/${article.slug}`,
            author: { '@type': 'Person', name: HELP_AUTHOR, url: HELP_AUTHOR_URL },
            datePublished: HELP_PUBLISHED,
            dateModified: HELP_REVIEWED,
            publisher: {
              '@type': 'Organization',
              name: 'AgentMesh',
              url: SITE,
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Help', item: `${SITE}/help` },
              { '@type': 'ListItem', position: 2, name: article.title, item: `${SITE}/help/${article.slug}` },
            ],
          },
        ]
      : undefined,
  });

  if (!article) return <Navigate to="/help" replace />;

  return (
    <Shell>
      <p className="mb-2 text-sm font-medium text-slate-500">{article.category}</p>
      <h1 className="mb-2 text-3xl font-bold">{article.title}</h1>
      {/* Must match the byline in scripts/dev/gen-content-pages.mjs — schema.org
          requires marked-up author/date to be visible, and if only the
          prerendered copy carries it the two versions disagree. */}
      <p className="mb-6 text-sm text-slate-500">
        By{' '}
        <a href="/about" className="underline underline-offset-2">
          {HELP_AUTHOR}
        </a>{' '}
        · Published <time dateTime={HELP_PUBLISHED}>{HELP_PUBLISHED}</time> · Reviewed{' '}
        <time dateTime={HELP_REVIEWED}>{HELP_REVIEWED}</time>
      </p>
      {article.body.map((block, i) => (
        <Block key={i} block={block} />
      ))}

      <div className="mt-10 border-t pt-6">
        <h2 className="mb-3 text-lg font-semibold">Related articles</h2>
        <ul className="space-y-2 text-sm">
          {helpArticles
            .filter((a) => a.slug !== article.slug)
            .filter((a) => a.category === article.category)
            .slice(0, 4)
            .map((a) => (
              <li key={a.slug}>
                <Link
                  to={`/help/${a.slug}`}
                  className="text-brand hover:underline"
                >
                  {a.title}
                </Link>
              </li>
            ))}
          <li>
            <Link to="/help" className="text-brand hover:underline">
              All help articles →
            </Link>
          </li>
        </ul>
      </div>
    </Shell>
  );
}
